const ordinal = (n: number) =>
    n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`;

export interface ShareResultItem {
    rank: number;
    /** Participant name (individual) or team name (group) */
    name: string;
    /** For individual events — the team/house this participant belongs to */
    teamName?: string | null;
    /** For group events — list of member names (captain first) */
    participants?: string[];
}

export interface ShareResultData {
    festName?: string;
    eventName: string;
    eventCode?: string;
    category?: string;
    eventType?: string;
    items: ShareResultItem[];
}

export function formatResultAsText(data: ShareResultData): string {
    const { festName, eventName, eventCode, category, eventType, items } = data;

    const validItems = items.filter(i => i.rank > 0 && i.rank <= 3 && i.name);

    const header = `${festName ? `${festName} — ` : ''}Result`;

    if (validItems.length === 0) {
        return [
            header, '',
            `Event: ${eventName}${eventCode ? ` (${eventCode})` : ''}`,
            '', 'No results available yet.',
        ].join('\n');
    }

    const lines: string[] = [header, ''];
    lines.push(`Event: ${eventName}${eventCode ? ` (${eventCode})` : ''}`);
    if (eventType) lines.push(`Type: ${eventType === 'GROUP' ? 'Group' : 'Individual'}`);
    if (category) lines.push(`Category: ${category}`);

    // Group items by rank (ties = same rank)
    const byRank = new Map<number, ShareResultItem[]>();
    for (const item of validItems) {
        const existing = byRank.get(item.rank) || [];
        byRank.set(item.rank, [...existing, item]);
    }

    const sortedRanks = Array.from(byRank.keys()).sort((a, b) => a - b);

    for (const rank of sortedRanks) {
        lines.push('');
        for (const item of byRank.get(rank)!) {
            const team = item.teamName ? ` (${item.teamName})` : '';
            lines.push(`${ordinal(rank)}: ${item.name}${team}`);
            if (item.participants && item.participants.length > 0) {
                lines.push(item.participants.join(', '));
            }
        }
    }

    return lines.join('\n');
}
